import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getQuestions } from '@/lib/api';
import { Archive, CircleDot, PlusCircle } from 'lucide-react';
import { formatNumber } from '@/lib/utils';

export default function QuestionsPage() {
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getQuestions();
        setQuestions(data || []);
      } catch (error) {
        console.error('Failed to fetch questions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-neutral-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <section>
        <h2 className="flex items-center gap-2 text-2xl font-semibold text-neutral-900">
          <Archive className="h-6 w-6 text-neutral-500" aria-hidden />
          Question Bank
        </h2>
        <p className="mt-1 text-sm text-neutral-500">
          Manage localized prompts, answer options, and scoring attributes for
          the global question bank.
        </p>
      </section>

      <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-900">
              <CircleDot className="h-5 w-5 text-neutral-500" aria-hidden />
              Active questions
            </h3>
            <p className="text-sm text-neutral-500">
              {questions.length === 0
                ? "No question assets stored yet."
                : `${formatNumber(questions.length)} reusable question assets.`}
            </p>
          </div>
          <Link
            to="/questions/new"
            className="inline-flex items-center gap-2 rounded-md bg-neutral-900 px-3 py-2 text-xs font-semibold text-white hover:bg-neutral-800"
          >
            <PlusCircle className="h-4 w-4" aria-hidden />
            Add / edit questions
          </Link>
        </div>

        <div className="mt-5 space-y-3">
          {questions.length === 0 ? (
            <div className="rounded-md border border-dashed border-neutral-200 p-4 text-sm text-neutral-500">
              No questions yet.{" "}
              <Link
                to="/questions/new"
                className="font-medium text-neutral-700 underline-offset-2 hover:text-neutral-900 hover:underline"
              >
                Create the first question
              </Link>{" "}
              to populate the bank.
            </div>
          ) : (
            questions.map((question, index) => (
              <article
                key={question.id ?? `question-${index}`}
                className="rounded-lg border border-neutral-100 bg-neutral-50 p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-neutral-800">
                      {question.text.en}
                    </p>
                    {question.text.hi || question.text.ru ? (
                      <p className="mt-1 text-xs text-neutral-500">
                        Localized ({question.text.hi ? "hi" : ""}{" "}
                        {question.text.ru ? "ru" : ""})
                      </p>
                    ) : null}
                  </div>
                  <div className="flex gap-3 text-xs text-neutral-600">
                    <span>Cooldown {question.cooldown}s</span>
                    <span>Timer {question.time}s</span>
                    <span>Marks {question.marks}</span>
                  </div>
                </div>
                <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                  {question.options.map((option: any, optionIndex: number) => (
                    <li
                      key={option.id ?? `option-${optionIndex}`}
                      className="rounded-md border border-neutral-200 bg-white px-3 py-2 text-xs text-neutral-600"
                    >
                      <span className="font-medium text-neutral-700">
                        {option.text.en}
                      </span>
                      {option.is_correct ? (
                        <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                          Correct
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

